import { CommonModule } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { PageDataPanelComponent } from 'src/app/shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from 'src/app/shared/page-layout/page-hero.component';
import { PageMetric } from 'src/app/shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from 'src/app/shared/page-layout/page-metric-strip.component';
import { PageSearchPanelComponent } from 'src/app/shared/page-layout/page-search-panel.component';
import { PageShellComponent } from 'src/app/shared/page-layout/page-shell.component';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { ContainerTerminalYardService } from '../container-terminal-yard.service';
import { ContainerYardEir, ContainerYardOccupancyOverview, ContainerYardVisit } from '../models/container-terminal-yard.model';

type YardBoardView = 'inventory' | 'occupancy' | 'slots';

interface OperationalSlotRow {
  key: string;
  facilityId?: number;
  facilityName?: string;
  areaId?: number;
  areaName?: string;
  slotCode?: string;
  containerNumber?: string;
  visit?: ContainerYardVisit;
  occupied: boolean;
  holdActive?: boolean;
  freeTimeExpired?: boolean;
}

interface YardFilterOption {
  label: string;
  value: number;
}

@Component({
  selector: 'app-yard-inventory',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    IftaLabelModule,
    InputGroupAddonModule,
    InputGroupModule,
    InputTextModule,
    ProgressBarModule,
    RouterLink,
    SelectModule,
    TableModule,
    TagModule,
    TooltipModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageSearchPanelComponent,
    PageDataPanelComponent
  ],
  templateUrl: './yard-inventory.component.html',
  styleUrl: './yard-inventory.component.css'
})
export class YardInventoryComponent implements OnInit {
  isLoading = false;
  visits: ContainerYardVisit[] = [];
  occupancy: ContainerYardOccupancyOverview = {};
  eirsByVisit = new Map<number, ContainerYardEir[]>();
  activeView: YardBoardView = 'inventory';

  readonly viewOptions: Array<{ label: string; value: YardBoardView; icon: string }> = [
    { label: 'Inventory', value: 'inventory', icon: 'pi pi-box' },
    { label: 'Occupancy', value: 'occupancy', icon: 'pi pi-th-large' },
    { label: 'Slots', value: 'slots', icon: 'pi pi-table' }
  ];

  filters = {
    facilityId: null as number | null,
    areaId: null as number | null,
    status: 'ACTIVE' as string | null,
    emptyFullStatus: null as string | null,
    containerNumber: '',
    billOfLadingNumber: '',
    bookingNumber: '',
    holdActive: null as boolean | null,
    freeTimeExpired: null as boolean | null
  };

  statusOptions = [
    { label: 'Active', value: 'ACTIVE' },
    { label: 'On Hold', value: 'ON_HOLD' },
    { label: 'Gated Out', value: 'GATED_OUT' },
    { label: 'Empty Returned', value: 'EMPTY_RETURNED' },
    { label: 'Cancelled', value: 'CANCELLED' }
  ];
  fullEmptyOptions = [
    { label: 'Full', value: 'FULL' },
    { label: 'Empty', value: 'EMPTY' },
    { label: 'Unknown', value: 'UNKNOWN' }
  ];
  booleanOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false }
  ];

  constructor(
    private yardService: ContainerTerminalYardService,
    private sessionStorageService: SessionStorageService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap.subscribe(params => {
      this.activeView = this.resolveView(params.get('view') || this.route.snapshot.data['defaultView']);
      this.filters.facilityId = this.numberParam(params.get('facilityId'));
      this.filters.areaId = this.numberParam(params.get('areaId'));
      this.filters.status = params.get('status') || this.filters.status;
      this.load();
    });
  }

  get metrics(): PageMetric[] {
    return [
      { label: 'Active Visits', value: this.occupancy.activeVisits || this.visits.length, tone: 'info' },
      { label: 'Occupied Slots', value: this.occupancy.occupiedSlots || 0, tone: 'warn' },
      { label: 'Available Slots', value: this.occupancy.availableSlots || 0, tone: 'success' },
      { label: 'On Hold', value: this.occupancy.holds || 0, tone: 'danger' },
      { label: 'Expired Free Time', value: this.occupancy.expiredFreeTimeCount || 0, tone: 'danger' }
    ];
  }

  get activeFilterCount(): number {
    return Object.values(this.filters).filter(value => value !== null && value !== '').length;
  }

  get facilityOptions(): YardFilterOption[] {
    const map = new Map<number, string>();
    (this.occupancy.rows || []).forEach(row => {
      if (row.facilityId) {
        map.set(row.facilityId, row.facilityName || `Facility #${row.facilityId}`);
      }
    });
    this.visits.forEach(visit => {
      if (visit.facilityId && !map.has(visit.facilityId)) {
        map.set(visit.facilityId, `Facility #${visit.facilityId}`);
      }
    });
    return Array.from(map.entries()).map(([value, label]) => ({ label, value }));
  }

  get areaOptions(): YardFilterOption[] {
    const map = new Map<number, string>();
    (this.occupancy.rows || [])
      .filter(row => !this.filters.facilityId || row.facilityId === this.filters.facilityId)
      .forEach(row => {
        if (row.areaId) {
          map.set(row.areaId, row.areaName || `Area #${row.areaId}`);
        }
      });
    this.visits
      .filter(visit => !this.filters.facilityId || visit.facilityId === this.filters.facilityId)
      .forEach(visit => {
        if (visit.currentAreaId && !map.has(visit.currentAreaId)) {
          map.set(visit.currentAreaId, visit.areaName || `Area #${visit.currentAreaId}`);
        }
      });
    return Array.from(map.entries()).map(([value, label]) => ({ label, value }));
  }

  get operationalSlotRows(): OperationalSlotRow[] {
    return this.visits
      .filter(visit => !!visit.currentSlotId || !!visit.slotCode || !!visit.currentLocationText)
      .map(visit => ({
        key: `${visit.id || visit.containerNumber || 'visit'}-${visit.currentSlotId || visit.slotCode || visit.currentLocationText || 'slot'}`,
        facilityId: visit.facilityId,
        facilityName: this.occupancy.rows?.find(row => row.facilityId === visit.facilityId)?.facilityName,
        areaId: visit.currentAreaId,
        areaName: visit.areaName,
        slotCode: visit.slotCode || visit.currentLocationText || 'Slot pending',
        containerNumber: visit.containerNumber,
        visit,
        occupied: !!visit.containerNumber,
        holdActive: !!visit.holdActive,
        freeTimeExpired: !!visit.freeTimeExpired
      }));
  }

  load(): void {
    const companyId = this.companyId();
    if (!companyId) {
      this.visits = [];
      this.occupancy = {};
      return;
    }
    this.isLoading = true;
    const params = this.buildParams(companyId);
    this.yardService.findInventory(params).subscribe({
      next: visits => {
        this.visits = visits || [];
        this.loadEirs();
        this.isLoading = false;
      },
      error: () => {
        this.visits = [];
        this.isLoading = false;
      }
    });
    this.yardService.occupancy(this.occupancyParams(companyId)).subscribe({
      next: overview => this.occupancy = overview || {},
      error: () => this.occupancy = {}
    });
  }

  clear(): void {
    this.filters = {
      facilityId: null,
      areaId: null,
      status: 'ACTIVE',
      emptyFullStatus: null,
      containerNumber: '',
      billOfLadingNumber: '',
      bookingNumber: '',
      holdActive: null,
      freeTimeExpired: null
    };
    this.syncViewQuery();
    this.load();
  }

  openCase(visit: ContainerYardVisit): void {
    if (visit.containerCaseId) {
      this.router.navigate(['/container-tracking/cases/view', visit.containerCaseId], { queryParams: { tab: 'storage' } });
    }
  }

  filterArea(areaId?: number, facilityId?: number): void {
    this.filters.facilityId = facilityId || this.filters.facilityId;
    this.filters.areaId = areaId || null;
    this.activeView = 'inventory';
    this.syncViewQuery();
    this.load();
  }

  onFacilityFilterChange(): void {
    if (this.filters.areaId && !this.areaOptions.some(option => option.value === this.filters.areaId)) {
      this.filters.areaId = null;
    }
  }

  switchView(view: YardBoardView): void {
    this.activeView = view;
    this.syncViewQuery();
  }

  recalculateCharges(visit: ContainerYardVisit): void {
    const companyId = this.companyId();
    if (!companyId || !visit.id) {
      return;
    }
    this.yardService.recalculateCharges(companyId, visit.id).subscribe({ next: () => this.load() });
  }

  latestEir(visit: ContainerYardVisit): ContainerYardEir | undefined {
    return visit.id ? this.eirsByVisit.get(visit.id)?.[0] : undefined;
  }

  openEir(visit: ContainerYardVisit): void {
    const companyId = this.companyId();
    const eir = this.latestEir(visit);
    if (!companyId || !eir?.id) {
      return;
    }
    window.open(this.yardService.eirPdfUrl(companyId, eir.id), '_blank');
  }

  severity(value?: string | boolean): string {
    if (value === true) return 'success';
    if (value === false) return 'secondary';
    switch ((value || '').toString().toUpperCase()) {
      case 'ACTIVE':
      case 'FULL':
      case 'GOOD':
      case 'COMPLETED':
        return 'success';
      case 'ON_HOLD':
      case 'DAMAGED':
      case 'EXPIRED':
        return 'danger';
      case 'EMPTY':
      case 'UNKNOWN':
        return 'warn';
      default:
        return 'info';
    }
  }

  money(value?: number): string {
    return `SAR ${(Number(value || 0)).toFixed(2)}`;
  }

  private loadEirs(): void {
    this.eirsByVisit.clear();
    this.visits.filter(visit => !!visit.id).forEach(visit => {
      this.yardService.findEirs(visit.id!).subscribe({
        next: eirs => this.eirsByVisit.set(visit.id!, eirs || []),
        error: () => this.eirsByVisit.set(visit.id!, [])
      });
    });
  }

  private buildParams(companyId: number): HttpParams {
    let params = new HttpParams().set('companyId', String(companyId));
    Object.entries(this.filters).forEach(([key, value]) => {
      if (value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });
    return params;
  }

  private occupancyParams(companyId: number): HttpParams {
    let params = new HttpParams().set('companyId', String(companyId));
    if (this.filters.facilityId) {
      params = params.set('facilityId', String(this.filters.facilityId));
    }
    return params;
  }

  private companyId(): number {
    return this.sessionStorageService.getCompany()?.id || 0;
  }

  private numberParam(value: string | null): number | null {
    if (!value) {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private resolveView(value: string | null): YardBoardView {
    return value === 'occupancy' || value === 'slots' ? value : 'inventory';
  }

  private syncViewQuery(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        view: this.activeView,
        facilityId: this.filters.facilityId || null,
        areaId: this.filters.areaId || null
      },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }
}
